import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Body2, Button, Caption1, Dialog, Input, InputPassword, InputUrl, Label } from "@activecollab/components";
import { Field, Form, Formik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";
import { useFocusInvalidField } from "~/components/Hooks/useFocusInvalidField";
import { SelectProjects } from "~/components/SelectProjects/SelectProjects";
import { useAddWebhookIntegrationMutation, useQueryData, useUpdateWebhookIntegrationMutation } from "~/repository";
import { SelectEventTypes } from "./SelectEventTypes";

export const WebhookFormDialog = ({ integration, open, onClose }) => {
  const { t } = useTranslation();
  const [showErrors, setShowErrors] = useState([]);

  const queryData = useQueryData(["integrations", "webhooks"]);

  const usedUrls = useMemo(() => {
    const { single = {} } = queryData;
    const { webhooks = [] } = single;
    if (integration) {
      return webhooks.filter((w) => w.id !== integration.id).map((w) => w.url);
    }
    return webhooks.map((w) => w.url);
  }, [integration, queryData]);

  const validationSchema = yup.object().shape({
    name: yup.string().max(100).required(),
    url: yup
      .string()
      .url()
      .max(1000)
      .required()
      .test("already-in-use", "Url already in use", function (value) {
        return !usedUrls.includes(value);
      }),
    secret: yup.string().max(191),
  });

  const { fieldRef, focusFirstInvalidField } = useFocusInvalidField();

  const updateMutation = useUpdateWebhookIntegrationMutation();
  const addMutation = useAddWebhookIntegrationMutation();

  const handleSubmit = useCallback(
    ({ filter_event_types, filter_projects, ...rest }) => {
      const attributes = {
        ...rest,
        filter_event_types: filter_event_types.join(","),
        filter_projects: filter_projects.join(","),
        is_enabled: integration ? integration.is_enabled : true,
      };
      if (integration) {
        updateMutation.mutate(
          { id: integration.id, ...attributes },
          {
            onSuccess: onClose,
          }
        );
      } else {
        addMutation.mutate(attributes, {
          onSuccess: onClose,
        });
      }
    },
    [addMutation, integration, onClose, updateMutation]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
    >
      <Formik
        initialValues={{
          name: integration ? integration.name : "",
          filter_event_types: integration ? integration.filter_event_types : [],
          filter_projects: integration ? integration.projects.map((p) => p.id) : [],
          url: integration ? integration.url : "",
          secret: integration ? integration.secret : "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, setFieldValue, handleSubmit, dirty }) => {
          const handleFormSubmit = (e) => {
            e.preventDefault();

            handleSubmit();
            if (Object.keys(errors)?.length) {
              setShowErrors(Object.keys(errors));
              return focusFirstInvalidField(errors, false, ["name", "url", "secret"]);
            }
          };
          return (
            <Form
              role="form"
              onSubmit={handleFormSubmit}
              autoComplete="off"
            >
              <Dialog.Title>{integration ? t("Edit Webhook") : t("Add Webhook")}</Dialog.Title>
              <Dialog.ContentDivider />
              <Dialog.Content className="tw-flex tw-flex-col tw-gap-4">
                <Field name="name">
                  {({ field, meta }) => (
                    <div className="tw-flex tw-flex-col tw-gap-1">
                      <Label
                        size="small"
                        htmlFor="name"
                      >
                        {t("Name")}*
                      </Label>
                      <Input
                        {...field}
                        ref={(el) => {
                          if (fieldRef) {
                            fieldRef.current[field.name] = el;
                          }
                        }}
                        data-testid="webhook-name"
                        className="tw-w-full tw-max-w-[360px]"
                        type="text"
                        id="name"
                        required
                        invalid={meta.error && meta.touched && showErrors.includes(field.name)}
                        onClick={() => {
                          setShowErrors((prev) => prev.filter((err) => err !== field.name));
                        }}
                      />
                      <Caption1 color="tertiary">{t("Add a label that'll help you recognize this webhook")}</Caption1>
                    </div>
                  )}
                </Field>
                <Field name="filter_event_types">
                  {({ field }) => (
                    <div className="tw-flex tw-flex-col tw-gap-1">
                      <Label
                        size="small"
                        htmlFor="filter_event_types"
                      >
                        {t("Send")}
                      </Label>
                      <SelectEventTypes
                        {...field}
                        id="filter_event_types"
                        onChange={(value) => {
                          setFieldValue("filter_event_types", value);
                        }}
                      />
                    </div>
                  )}
                </Field>
                <Field name="filter_projects">
                  {({ field }) => (
                    <div className="tw-flex tw-flex-col tw-gap-1">
                      <Label
                        size="small"
                        htmlFor="filter_projects"
                      >
                        {t("From")}
                      </Label>
                      <SelectProjects
                        {...field}
                        id="filter_projects"
                        onChange={(value) => {
                          setFieldValue("filter_projects", value);
                        }}
                      />
                    </div>
                  )}
                </Field>
                <Field name="url">
                  {({ field, meta }) => (
                    <div className="tw-flex tw-flex-col tw-gap-1">
                      <Label
                        size="small"
                        htmlFor="url"
                      >
                        {t("Target URL")}*
                      </Label>
                      <InputUrl
                        {...field}
                        ref={(el) => {
                          if (fieldRef) {
                            fieldRef.current[field.name] = el;
                          }
                        }}
                        maxLength={1000}
                        className="tw-w-full tw-max-w-[360px]"
                        required
                        onChange={(value) => setFieldValue("url", value)}
                        invalid={meta.error && meta.touched && showErrors.includes(field.name)}
                        onClick={() => {
                          setShowErrors((prev) => prev.filter((err) => err !== field.name));
                        }}
                      />
                      <Caption1 color="tertiary">{t("POST request will be sent to this URL")}</Caption1>
                    </div>
                  )}
                </Field>
                <Field name="secret">
                  {({ field, meta }) => (
                    <div className="tw-flex tw-flex-col tw-gap-1 tw-w-full tw-max-w-[360px]">
                      <Label
                        size="small"
                        htmlFor="secret"
                      >
                        {t("Secret")}
                      </Label>
                      <InputPassword
                        {...field}
                        autoComplete="new-password"
                        ref={(el) => {
                          if (fieldRef) {
                            fieldRef.current[field.name] = el;
                          }
                        }}
                        wrapperClassName="tw-w-full tw-max-w-full"
                        className="tw-w-full"
                        id="secret"
                        name="secret"
                        onChange={(value) => setFieldValue("secret", value)}
                        invalid={meta.error && meta.touched && showErrors.includes(field.name)}
                        onClick={() => {
                          setShowErrors((prev) => prev.filter((err) => err !== field.name));
                        }}
                      />
                    </div>
                  )}
                </Field>
                <Body2>
                  {t(
                    "An additional field that can be specified for authentication purposes. This will be included in the X-Angie-WebhookSecret HTTP header of every POST request (optional)"
                  )}
                </Body2>
              </Dialog.Content>
              <Dialog.ContentDivider />
              <Dialog.Actions>
                <Button
                  type="submit"
                  className="tw-mr-3"
                  disabled={isSubmitting || (integration && !dirty)}
                >
                  {integration ? t("Save") : t("Add")}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                >
                  {t("Cancel")}
                </Button>
              </Dialog.Actions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

WebhookFormDialog.propTypes = {
  integration: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};
